// The client-side slicer values a title or text token quotes: every slicer on the
// board, by key, mapped to the variables its kind offers. Pure, so the browser, the PDF
// Lambda and the node runner share it; the helpers that need the roster or the antd
// constants come in through `deps`.

import dayjs from "dayjs";
import { DEFAULT_PRESET_OFFSET, PRESET_OFFSETS, isSessionWindow } from "../Processing/datePresets.mjs";
import { kindWord, variablesForControls } from "./resolveTemplate.mjs";
import { assignKeys } from "./slicerKeys.mjs";

const DATE_FORMAT = "YYYY-MM-DD";
const LIST_SEPARATOR = ", ";
const FULL_TEAM = "Full Team";
const UNKNOWN = "Unknown";

// `resolveSubjectNames` returns the raw id for an entity it cannot find.
const RAW_ID_RE = /^(player|pos|group|drill)-/;

function named(names) {
  return names.map((name) => (!name || RAW_ID_RE.test(name) ? UNKNOWN : name));
}

function athleteName(controls, misc, deps) {
  const name = deps.firstAthleteName(controls, misc);
  if (name == null || name === "") return "";
  return RAW_ID_RE.test(name) ? UNKNOWN : name;
}

function dateValues(controls, misc, deps) {
  const today = dayjs(deps.today);
  const day = (offset) => today.add(offset, "day").format(DATE_FORMAT);

  if (!controls.includes("date")) {
    // A legacy single-date slicer: both edges are the picked day.
    const picked = misc.dateMode === "absolute" ? misc.fixedDate || null : day(misc.dateOffset ?? 0);
    return { StartDate: picked, EndDate: picked };
  }

  // The same rules as buildDateFilter, applied to today's date.
  const preset = misc.dateRangePreset ?? misc.dateRange;
  const presetOffset = preset ? PRESET_OFFSETS[preset] ?? DEFAULT_PRESET_OFFSET : DEFAULT_PRESET_OFFSET;
  let start;
  let end;
  if (!misc.showAdvancedDate) {
    start = isSessionWindow(misc) ? null : day(presetOffset);
    end = day(0);
  } else {
    const startType = misc.startDateType ?? "relative";
    const endType = misc.endDateType ?? "relative";
    start =
      startType === "latest_n"
        ? null
        : startType === "absolute"
          ? misc.startDateAbsolute || null
          : day(misc.startDateOffset ?? presetOffset);
    end =
      startType === "latest_n"
        ? day(0)
        : endType === "absolute"
          ? misc.endDateAbsolute || null
          : day(misc.endDateOffset ?? 0);
  }
  return {
    StartDate: start,
    EndDate: end,
    DateRange: deps.dateRangeLabel(misc),
    DayFilter: (misc.dayFilter ?? []).map((tag) => deps.dayLabel(tag) ?? tag).join(LIST_SEPARATOR),
  };
}

function selectionValues(subjectIds, drillIds, athlete, deps) {
  const picked = subjectIds.filter((id) => id !== "full-team");
  const subjects = picked.length ? named(deps.subjectNames(picked, [])) : [];
  const drills = drillIds.length ? named(deps.subjectNames([], drillIds)) : [];
  return {
    Subjects: subjects.length ? subjects.join(LIST_SEPARATOR) : FULL_TEAM,
    SubjectCount: String(subjects.length),
    Athlete: athlete,
    Drills: drills.join(LIST_SEPARATOR),
  };
}

function subjectValues(controls, misc, deps) {
  const athlete = athleteName(controls, misc, deps);
  if (!controls.includes("subject")) {
    // A legacy single-player slicer: every variable reads its one athlete.
    return { Subjects: athlete || FULL_TEAM, SubjectCount: athlete ? "1" : "0", Athlete: athlete };
  }
  return selectionValues(misc.selectedSubjects ?? [], misc.selectedDrills ?? [], athlete, deps);
}

// The engine's answer replaces the client dates and roots. The roots are the same ids
// the client map reads, so the same names apply. DateRange and DayFilter stay client-side.
function engineValues(engine, client, deps) {
  const dates = engine.dates ?? [];
  const athlete = engine.athlete ? (named(deps.subjectNames([engine.athlete], []))[0] ?? UNKNOWN) : "";
  return {
    StartDate: engine.start_date ?? dates[0] ?? null,
    EndDate: engine.end_date ?? dates[dates.length - 1] ?? client.EndDate ?? null,
    ...selectionValues(engine.subjects ?? [], engine.drills ?? [], athlete, deps),
  };
}

/**
 * Build the slicer value map `resolveTemplate` reads.
 *
 * `slicers` are `{ key, label, controls, misc, engine? }` in widget order; a slicer
 * without a valid key gets one from `assignKeys`. `engine` is the backend's
 * `SlicerResponseData` for the slicer, when the dashboard data carries one: its dates
 * and roots replace the client values. `deps` are `{ today, dateRangeLabel(misc),
 * subjectNames(subjectIds, drillIds), firstAthleteName(controls, misc), dayLabel(tag) }`.
 * A name that comes back as a raw id prints "Unknown". A variable with no value yet
 * (the start of a session window before the engine answers) is null.
 */
export function buildSlicerValues(slicers, deps) {
  const keys = assignKeys(slicers);
  const out = {};
  slicers.forEach((slicer, i) => {
    const controls = slicer.controls ?? [];
    const misc = slicer.misc ?? {};
    const client = {
      ...(controls.some((c) => c === "date" || c === "single_date") ? dateValues(controls, misc, deps) : {}),
      ...(controls.some((c) => c === "subject" || c === "single_player") ? subjectValues(controls, misc, deps) : {}),
    };
    const all = slicer.engine ? { ...client, ...engineValues(slicer.engine, client, deps) } : client;
    const values = {};
    for (const variable of variablesForControls(controls)) values[variable] = all[variable] ?? null;
    out[keys[i]] = { kind: kindWord(controls), label: slicer.label ?? "", values };
  });
  return out;
}
